import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../contexts/ThemeContext';
import {
  FiUser, FiSettings, FiBell, FiLock, FiDatabase, FiGlobe,
  FiMoon, FiSun, FiMail, FiPhone, FiKey, FiShield, FiEye,
  FiEyeOff, FiSave, FiRefreshCw, FiTrash2, FiEdit2, FiCheck,
  FiX, FiToggleLeft, FiToggleRight, FiVolume2, FiVolumeX
} from 'react-icons/fi';
import { BiBot, BiBrain } from 'react-icons/bi';
import { BsRobot } from 'react-icons/bs';
import toast from 'react-hot-toast';
import api from '../utils/api';

interface SettingsSection {
  id: string;
  title: string;
  icon: React.ReactNode;
  description: string;
}

const Settings: React.FC = () => {
  const { colorScheme, setColorScheme, colors } = useTheme();
  const [activeSection, setActiveSection] = useState('profile');
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [notifications, setNotifications] = useState({
    email: true,
    push: true,
    sms: false,
    sound: true
  });
  const [notifEmail, setNotifEmail] = useState('');
  const [notifPhone, setNotifPhone] = useState('');
  const [pushSupported, setPushSupported] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [profileData, setProfileData] = useState({
    firstName: 'John',
    lastName: 'Doe',
    email: 'john.doe@example.com',
    phone: '+1 (555) 123-4567',
    timezone: 'America/New_York',
    language: 'English'
  });

  const [integrations, setIntegrations] = useState({
    openai: { enabled: false, apiKey: '', model: 'gpt-4', hasStoredKey: false },
    anthropic: { enabled: false, apiKey: '', model: 'claude-3-sonnet', hasStoredKey: false },
    google: { enabled: false, apiKey: '', model: 'gemini-pro', hasStoredKey: false },
    azure: { enabled: false, endpoint: '', apiKey: '', model: 'gpt-4', hasStoredKey: false },
    ollama: { enabled: false, endpoint: 'http://localhost:11434', model: 'llama2', hasStoredKey: false },
    huggingface: { enabled: false, apiKey: '', model: 'microsoft/DialoGPT-medium', hasStoredKey: false },
    salesforce: { enabled: false, instanceUrl: '', apiKey: '', username: '', hasStoredKey: false },
    hubspot: { enabled: false, apiKey: '', portalId: '', hasStoredKey: false },
    pipedrive: { enabled: false, apiKey: '', domain: '', hasStoredKey: false },
    zoho: { enabled: false, apiKey: '', domain: '', email: '', hasStoredKey: false },
    custom_crm: { enabled: false, apiEndpoint: '', apiKey: '', authMethod: 'bearer', hasStoredKey: false }
  });

  const [showApiKey, setShowApiKey] = useState<{[key: string]: boolean}>({});
  const [phoneSettings, setPhoneSettings] = useState({
    primaryPhone: '',
    secondaryPhone: '',
    smsEnabled: true,
    voiceEnabled: true,
    whatsappEnabled: false,
    provider: 'twilio', // 'twilio', 'vonage', 'custom'
    providerConfig: {
      accountSid: '',
      authToken: '',
      phoneNumber: ''
    }
  });

  const sections: SettingsSection[] = [
    {
      id: 'profile',
      title: 'Profile',
      icon: <FiUser size={20} />,
      description: 'Manage your personal information'
    },
    {
      id: 'security',
      title: 'Security',
      icon: <FiLock size={20} />,
      description: 'Password and authentication settings'
    },
    {
      id: 'notifications',
      title: 'Notifications',
      icon: <FiBell size={20} />,
      description: 'Configure notification preferences'
    },
    {
      id: 'appearance',
      title: 'Appearance',
      icon: <FiSettings size={20} />,
      description: 'Customize the interface'
    },
    {
      id: 'integrations',
      title: 'Integrations',
      icon: <FiDatabase size={20} />,
      description: 'Connect external services'
    },
    {
      id: 'phone',
      title: 'Phone Settings',
      icon: <FiPhone size={20} />,
      description: 'Configure phone number and voice settings'
    },
    {
      id: 'advanced',
      title: 'Advanced',
      icon: <FiGlobe size={20} />,
      description: 'Advanced configuration options'
    }
  ];

  const toggleNotification = (type: keyof typeof notifications) => {
    setNotifications(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
    toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} notifications ${notifications[type] ? 'disabled' : 'enabled'}`);
  };

  useEffect(() => {
    setPushSupported('serviceWorker' in navigator && 'PushManager' in window);
    (async () => {
      try {
        const { data } = await api.get('/notifications/preferences');
        setNotifications({
          email: !!data?.email?.enabled,
          push: !!data?.push?.enabled,
          sms: !!data?.sms?.enabled,
          sound: !!data?.sound?.enabled,
        });
        setNotifEmail(data?.email?.address || '');
        setNotifPhone(data?.sms?.phone || '');
      } catch {}
    })();

    // Load stored integration keys
    (async () => {
      try {
        const { data } = await api.get('/api/integrations/keys');
        if (data && Array.isArray(data.keys)) {
          const updatedIntegrations = { ...integrations };
          data.keys.forEach((key: any) => {
            if (updatedIntegrations[key.provider as keyof typeof integrations]) {
              (updatedIntegrations[key.provider as keyof typeof integrations] as any).hasStoredKey = true;
              (updatedIntegrations[key.provider as keyof typeof integrations] as any).enabled = key.isActive;
            }
          });
          setIntegrations(updatedIntegrations);
        }
      } catch (error) {
        console.error('Failed to load integration keys:', error);
      }
    })();
  }, []);

  const saveNotificationPrefs = async () => {
    try {
      const payload = {
        email: { enabled: notifications.email, address: notifEmail },
        push: { enabled: notifications.push },
        sms: { enabled: notifications.sms, phone: notifPhone },
        sound: { enabled: notifications.sound },
        events: { meetingStart: true, meetingSummaryReady: true, newLead: true, taskDue: true }
      };
      await api.post('/notifications/preferences', payload);
      toast.success('Notification preferences saved');
    } catch {
      toast.error('Failed to save notification preferences');
    }
  };

  const subscribePush = async () => {
    if (!pushSupported) return;
    const reg = await navigator.serviceWorker.ready;
    const vapid = (import.meta as any).env?.VITE_VAPID_PUBLIC_KEY || '';
    const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: vapid ? urlBase64ToUint8Array(vapid) : undefined });
    await api.post('/notifications/push/subscribe', sub);
    toast.success('Push subscribed');
  };

  const sendTestEmail = async () => { try { await api.post('/notifications/test/email', { address: notifEmail }); toast.success('Test email sent'); } catch { toast.error('Email test failed'); } };
  const sendTestPush = async () => { try { await api.post('/notifications/test/push', {}); toast.success('Test push sent'); } catch { toast.error('Push test failed'); } };
  const sendTestSMS = async () => { try { await api.post('/notifications/test/sms', { phone: notifPhone }); toast.success('Test SMS sent'); } catch { toast.error('SMS test failed'); } };

  function urlBase64ToUint8Array(base64String: string) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
  }

  const toggleIntegration = (provider: keyof typeof integrations) => {
    setIntegrations(prev => ({
      ...prev,
      [provider]: { ...prev[provider], enabled: !prev[provider].enabled }
    }));
    toast.success(`${provider.toUpperCase()} ${integrations[provider].enabled ? 'disconnected' : 'connected'}`);
  };

  const updateIntegrationField = (provider: keyof typeof integrations, field: string, value: string) => {
    setIntegrations(prev => ({
      ...prev,
      [provider]: { ...prev[provider], [field]: value }
    }));
  };

  const toggleApiKeyVisibility = (provider: string) => {
    setShowApiKey(prev => ({ ...prev, [provider]: !prev[provider] }));
  };

  const saveIntegrationKey = async (provider: keyof typeof integrations) => {
    try {
      const integration = integrations[provider];
      const config: Record<string, any> = {};

      // Build config object based on provider
      Object.keys(integration).forEach(key => {
        if (key !== 'apiKey' && key !== 'enabled' && key !== 'hasStoredKey') {
          config[key] = (integration as any)[key];
        }
      });

      await api.post('/api/integrations/keys', {
        provider,
        keyType: 'api_key',
        value: 'apiKey' in integration ? integration.apiKey : '',
        config
      });

      // Update state to show key is stored
      setIntegrations(prev => ({
        ...prev,
        [provider]: { ...prev[provider], hasStoredKey: true, apiKey: '' }
      }));

      toast.success(`${provider.toUpperCase()} key saved securely!`);
    } catch (error) {
      toast.error(`Failed to save ${provider.toUpperCase()} key`);
      console.error('Save integration key error:', error);
    }
  };

  const testIntegrationConnection = async (provider: keyof typeof integrations) => {
    try {
      const response = await api.post('/api/integrations/test', {
        provider,
        keyType: 'api_key'
      });

      if (response.data.success) {
        toast.success(response.data.message || `${provider.toUpperCase()} connection successful!`);
      } else {
        toast.error(response.data.message || `${provider.toUpperCase()} connection failed`);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || `Failed to test ${provider.toUpperCase()} connection`);
      console.error('Test integration error:', error);
    }
  };

  const deleteIntegrationKey = async (provider: keyof typeof integrations) => {
    try {
      await api.delete(`/api/integrations/keys/${provider}/api_key`);

      setIntegrations(prev => ({
        ...prev,
        [provider]: { ...prev[provider], hasStoredKey: false, enabled: false }
      }));

      toast.success(`${provider.toUpperCase()} key deleted`);
    } catch (error) {
      toast.error(`Failed to delete ${provider.toUpperCase()} key`);
      console.error('Delete integration key error:', error);
    }
  };

  const getColorClasses = () => {
    return colors;
  };

  const renderProfileSection = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="space-y-6"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">First Name</label>
          <input
            type="text"
            value={profileData.firstName}
            onChange={(e) => setProfileData(prev => ({ ...prev, firstName: e.target.value }))}
            className={`w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:${getColorClasses().border}`}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Last Name</label>
          <input
            type="text"
            value={profileData.lastName}
            onChange={(e) => setProfileData(prev => ({ ...prev, lastName: e.target.value }))}
            className={`w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:${getColorClasses().border}`}
          />
        </div>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
        <input
          type="email"
          value={profileData.email}
          onChange={(e) => setProfileData(prev => ({ ...prev, email: e.target.value }))}
          className={`w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:${getColorClasses().border}`}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">Phone</label>
        <input
          type="tel"
          value={profileData.phone}
          onChange={(e) => setProfileData(prev => ({ ...prev, phone: e.target.value }))}
          className={`w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:${getColorClasses().border}`}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Timezone</label>
          <select
            value={profileData.timezone}
            onChange={(e) => setProfileData(prev => ({ ...prev, timezone: e.target.value }))}
            className={`w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:${getColorClasses().border}`}
          >
            <option>America/New_York</option>
            <option>America/Los_Angeles</option>
            <option>Europe/London</option>
            <option>Asia/Tokyo</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Language</label>
          <select
            value={profileData.language}
            onChange={(e) => setProfileData(prev => ({ ...prev, language: e.target.value }))}
            className={`w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:${getColorClasses().border}`}
          >
            <option>English</option>
            <option>Spanish</option>
            <option>French</option>
            <option>German</option>
          </select>
        </div>
      </div>
    </motion.div>
  );

  const renderSecuritySection = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="space-y-6"
    >
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">Current Password</label>
        <div className="relative">
          <input
            type={showPassword ? 'text' : 'password'}
            className={`w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:${getColorClasses().border} pr-12`}
            placeholder="Enter current password"
            autoComplete="current-password"
          />
          <button
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
          >
            {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
          </button>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">New Password</label>
        <input
          type="password"
          className={`w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:${getColorClasses().border}`}
          placeholder="Enter new password"
          autoComplete="new-password"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">Confirm Password</label>
        <input
          type="password"
          className={`w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:${getColorClasses().border}`}
          placeholder="Confirm new password"
          autoComplete="new-password"
        />
      </div>

      <div className="p-4 bg-gray-800 rounded-lg">
        <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
          <FiShield className="text-green-400" />
          Two-Factor Authentication
        </h4>
        <p className="text-gray-400 text-sm mb-4">Add an extra layer of security to your account</p>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="px-4 py-2 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600"
        >
          Enable 2FA
        </motion.button>
      </div>
    </motion.div>
  );

  const renderNotificationsSection = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="space-y-6"
    >
      {Object.entries(notifications).map(([type, enabled]) => (
        <div key={type} className="flex items-center justify-between p-4 bg-gray-800 rounded-lg">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${
              type === 'email' ? 'bg-blue-500/20' :
              type === 'push' ? 'bg-green-500/20' :
              type === 'sms' ? 'bg-purple-500/20' : 'bg-yellow-500/20'
            }`}>
              {type === 'email' ? <FiMail className="text-blue-400" size={16} /> :
               type === 'push' ? <FiBell className="text-green-400" size={16} /> :
               type === 'sms' ? <FiPhone className="text-purple-400" size={16} /> :
               <FiVolume2 className="text-yellow-400" size={16} />}
            </div>
            <div>
              <h4 className="text-white font-medium capitalize">{type} Notifications</h4>
              <p className="text-gray-400 text-sm">
                {type === 'email' ? 'Receive notifications via email' :
                 type === 'push' ? 'Browser push notifications' :
                 type === 'sms' ? 'SMS text message alerts' :
                 'Sound notifications'}
              </p>
            </div>
          </div>
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => toggleNotification(type as keyof typeof notifications)}
            className={`text-2xl ${enabled ? getColorClasses().accent : 'text-gray-600'}`}
          >
            {enabled ? <FiToggleRight /> : <FiToggleLeft />}
          </motion.button>
        </div>
      ))}

      {/* Email address and test */}
      {notifications.email && (
        <div className="p-4 bg-gray-800 rounded-lg">
          <label className="block text-sm font-medium text-gray-300 mb-2">Email Address</label>
          <div className="flex gap-2">
            <input value={notifEmail} onChange={(e)=>setNotifEmail(e.target.value)} placeholder="you@example.com" className="flex-1 px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white" />
            <button onClick={sendTestEmail} className="px-4 py-2 rounded bg-gray-700 text-white">Send Test Email</button>
          </div>
        </div>
      )}

      {/* Push subscribe/test */}
      {notifications.push && (
        <div className="p-4 bg-gray-800 rounded-lg">
          <div className="flex gap-2">
            <button onClick={subscribePush} disabled={!pushSupported} className="px-4 py-2 rounded bg-gray-700 text-white disabled:opacity-50">{pushSupported ? 'Subscribe to Push' : 'Push Not Supported'}</button>
            <button onClick={sendTestPush} className="px-4 py-2 rounded bg-gray-700 text-white">Send Test Push</button>
          </div>
        </div>
      )}

      {/* SMS phone and test */}
      {notifications.sms && (
        <div className="p-4 bg-gray-800 rounded-lg">
          <label className="block text-sm font-medium text-gray-300 mb-2">SMS Phone</label>
          <div className="flex gap-2">
            <input value={notifPhone} onChange={(e)=>setNotifPhone(e.target.value)} placeholder="+15551234567" className="flex-1 px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white" />
            <button onClick={sendTestSMS} className="px-4 py-2 rounded bg-gray-700 text-white">Send Test SMS</button>
          </div>
        </div>
      )}

      <div className="pt-4">
        <button onClick={saveNotificationPrefs} className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg">Save Notification Settings</button>
      </div>
    </motion.div>
  );

  const renderAppearanceSection = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="space-y-6"
    >
      <div className="p-4 bg-gray-800 rounded-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-500/20">
              {isDarkMode ? <FiMoon className="text-purple-400" size={16} /> : <FiSun className="text-yellow-400" size={16} />}
            </div>
            <div>
              <h4 className="text-white font-medium">Theme</h4>
              <p className="text-gray-400 text-sm">
                {isDarkMode ? 'Dark mode enabled' : 'Light mode enabled'}
              </p>
            </div>
          </div>
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => {
              setIsDarkMode(!isDarkMode);
              toast.success(`Switched to ${!isDarkMode ? 'dark' : 'light'} mode`);
            }}
            className={`text-2xl ${isDarkMode ? getColorClasses().accent : 'text-gray-600'}`}
          >
            {isDarkMode ? <FiToggleRight /> : <FiToggleLeft />}
          </motion.button>
        </div>
      </div>

      <div className="p-4 bg-gray-800 rounded-lg">
        <h4 className="text-white font-medium mb-4">Color Scheme</h4>
        <div className="grid grid-cols-4 gap-3">
          {[
            { name: 'Purple', id: 'purple', colors: 'from-purple-500 to-pink-500' },
            { name: 'Blue', id: 'blue', colors: 'from-blue-500 to-cyan-500' },
            { name: 'Green', id: 'green', colors: 'from-green-500 to-emerald-500' },
            { name: 'Orange', id: 'orange', colors: 'from-orange-500 to-red-500' }
          ].map((theme) => (
            <motion.button
              key={theme.name}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className={`h-12 rounded-lg bg-gradient-to-r ${theme.colors} flex items-center justify-center text-white font-medium relative ${
                colorScheme === theme.id ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-800' : ''
              }`}
              onClick={() => {
                setColorScheme(theme.id as any);
                toast.success(`${theme.name} theme applied`);
              }}
            >
              {theme.name}
              {colorScheme === theme.id && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute -top-1 -right-1 bg-white text-gray-900 rounded-full w-5 h-5 flex items-center justify-center text-xs"
                >
                  <FiCheck size={12} />
                </motion.div>
              )}
            </motion.button>
          ))}
        </div>
      </div>
    </motion.div>
  );

  const renderIntegrationsSection = () => {
    const aiProviders = [
      {
        id: 'openai',
        name: 'OpenAI',
        description: 'GPT-4, GPT-3.5 Turbo models',
        icon: '🤖',
        color: 'from-green-500 to-emerald-500',
        fields: [
          { name: 'apiKey', label: 'API Key', type: 'password', placeholder: 'sk-...' },
          { name: 'model', label: 'Model', type: 'select', options: ['gpt-4', 'gpt-3.5-turbo', 'gpt-4-turbo'] }
        ]
      },
      {
        id: 'anthropic',
        name: 'Anthropic',
        description: 'Claude 3 Sonnet, Claude 3 Haiku models',
        icon: '🧠',
        color: 'from-purple-500 to-pink-500',
        fields: [
          { name: 'apiKey', label: 'API Key', type: 'password', placeholder: 'sk-ant-...' },
          { name: 'model', label: 'Model', type: 'select', options: ['claude-3-sonnet', 'claude-3-haiku', 'claude-3-opus'] }
        ]
      },
      {
        id: 'google',
        name: 'Google AI',
        description: 'Gemini Pro, PaLM models',
        icon: '🌟',
        color: 'from-blue-500 to-cyan-500',
        fields: [
          { name: 'apiKey', label: 'API Key', type: 'password', placeholder: 'AI...' },
          { name: 'model', label: 'Model', type: 'select', options: ['gemini-pro', 'gemini-pro-vision', 'text-bison'] }
        ]
      },
      {
        id: 'azure',
        name: 'Azure OpenAI',
        description: 'Enterprise-grade OpenAI models',
        icon: '☁️',
        color: 'from-indigo-500 to-purple-500',
        fields: [
          { name: 'endpoint', label: 'Endpoint', type: 'text', placeholder: 'https://your-resource.openai.azure.com/' },
          { name: 'apiKey', label: 'API Key', type: 'password', placeholder: 'Your Azure API key' },
          { name: 'model', label: 'Deployment Name', type: 'text', placeholder: 'gpt-4' }
        ]
      },
      {
        id: 'ollama',
        name: 'Ollama',
        description: 'Local models (Llama 2, Mistral, etc.)',
        icon: '🏠',
        color: 'from-orange-500 to-red-500',
        fields: [
          { name: 'endpoint', label: 'Endpoint', type: 'text', placeholder: 'http://localhost:11434' },
          { name: 'model', label: 'Model', type: 'select', options: ['llama2', 'mistral', 'codellama', 'vicuna'] }
        ]
      },
      {
        id: 'huggingface',
        name: 'Hugging Face',
        description: 'Open-source models and transformers',
        icon: '🤗',
        color: 'from-yellow-500 to-orange-500',
        fields: [
          { name: 'apiKey', label: 'API Token', type: 'password', placeholder: 'hf_...' },
          { name: 'model', label: 'Model', type: 'text', placeholder: 'microsoft/DialoGPT-medium' }
        ]
      }
    ];

    const crmProviders = [
      {
        id: 'salesforce',
        name: 'Salesforce',
        description: 'Connect leads and contacts to Salesforce CRM',
        icon: '☁️',
        color: 'from-blue-600 to-blue-800',
        fields: [
          { name: 'instanceUrl', label: 'Instance URL', type: 'text', placeholder: 'https://your-domain.salesforce.com' },
          { name: 'apiKey', label: 'Access Token', type: 'password', placeholder: 'Your Salesforce access token' },
          { name: 'username', label: 'Username', type: 'text', placeholder: 'your-email@domain.com' }
        ]
      },
      {
        id: 'hubspot',
        name: 'HubSpot',
        description: 'Sync contacts and deals with HubSpot CRM',
        icon: '🧡',
        color: 'from-orange-500 to-red-500',
        fields: [
          { name: 'apiKey', label: 'Private App Access Token', type: 'password', placeholder: 'pat-na1-...' },
          { name: 'portalId', label: 'Portal ID', type: 'text', placeholder: '12345678' }
        ]
      },
      {
        id: 'pipedrive',
        name: 'Pipedrive',
        description: 'Manage deals and contacts in Pipedrive',
        icon: '🚀',
        color: 'from-green-500 to-teal-500',
        fields: [
          { name: 'apiKey', label: 'API Token', type: 'password', placeholder: 'Your Pipedrive API token' },
          { name: 'domain', label: 'Company Domain', type: 'text', placeholder: 'your-company.pipedrive.com' }
        ]
      },
      {
        id: 'zoho',
        name: 'Zoho CRM',
        description: 'Integrate with Zoho CRM for lead management',
        icon: '🔷',
        color: 'from-indigo-500 to-purple-600',
        fields: [
          { name: 'apiKey', label: 'Auth Token', type: 'password', placeholder: 'Your Zoho auth token' },
          { name: 'domain', label: 'Domain', type: 'select', options: ['com', 'eu', 'in', 'com.au', 'jp'] },
          { name: 'email', label: 'Email', type: 'text', placeholder: 'your-email@domain.com' }
        ]
      },
      {
        id: 'custom_crm',
        name: 'Custom CRM',
        description: 'Connect to any CRM via REST API',
        icon: '🔧',
        color: 'from-gray-500 to-slate-600',
        fields: [
          { name: 'apiEndpoint', label: 'API Endpoint', type: 'text', placeholder: 'https://your-crm.com/api/v1' },
          { name: 'apiKey', label: 'API Key', type: 'password', placeholder: 'Your API key' },
          { name: 'authMethod', label: 'Auth Method', type: 'select', options: ['bearer', 'basic', 'api_key'] }
        ]
      }
    ];

    return (
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        className="space-y-6"
      >
        <div className="mb-6 space-y-4">
          <div className="p-4 bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-lg border border-green-500/20">
            <h4 className="text-white font-semibold mb-2 flex items-center gap-2">
              <FiShield className="text-green-400" />
              🔒 Enterprise-Grade Security
            </h4>
            <p className="text-gray-300 text-sm">
              Your API keys are encrypted with AES-256-GCM and stored securely in our backend database.
              Keys are never exposed to the browser or transmitted in network requests. All AI requests
              are proxied through our secure backend to protect your credentials.
            </p>
          </div>
          <div className="p-4 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-lg border border-blue-500/20">
            <h4 className="text-white font-semibold mb-2 flex items-center gap-2">
              💰 Save Money with Your Own API Keys
            </h4>
            <p className="text-gray-300 text-sm">
              Connect your own AI provider accounts to use your API credits directly.
              This can significantly reduce costs compared to using third-party services.
            </p>
          </div>
        </div>

        {/* AI Providers Section */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
            🤖 AI Providers
          </h3>
          <div className="space-y-4">
            {aiProviders.map((provider) => (
          <motion.div
            key={provider.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-6 bg-gray-800/50 rounded-lg border border-gray-700"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`p-3 rounded-lg bg-gradient-to-r ${provider.color} text-white text-xl`}>
                  {provider.icon}
                </div>
                <div>
                  <h4 className="text-white font-semibold text-lg">{provider.name}</h4>
                  <p className="text-gray-400 text-sm">{provider.description}</p>
                </div>
              </div>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => toggleIntegration(provider.id as keyof typeof integrations)}
                className={`text-2xl ${integrations[provider.id as keyof typeof integrations].enabled ? getColorClasses().accent : 'text-gray-600'}`}
              >
                {integrations[provider.id as keyof typeof integrations].enabled ? <FiToggleRight /> : <FiToggleLeft />}
              </motion.button>
            </div>

            <AnimatePresence>
              {integrations[provider.id as keyof typeof integrations].enabled && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="space-y-4 pt-4 border-t border-gray-700"
                >
                  {provider.fields.map((field) => (
                    <div key={field.name}>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        {field.label}
                      </label>
                      {field.type === 'select' ? (
                        <select
                          value={String(integrations[provider.id as keyof typeof integrations][field.name as keyof typeof integrations[keyof typeof integrations]] || '')}
                          onChange={(e) => updateIntegrationField(provider.id as keyof typeof integrations, field.name, e.target.value)}
                          className={`w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:${getColorClasses().border}`}
                        >
                          {field.options?.map(option => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      ) : (
                        <div className="space-y-2">
                          {field.type === 'password' && integrations[provider.id as keyof typeof integrations].hasStoredKey && (
                            <div className="flex items-center gap-2 text-sm text-green-400">
                              <FiShield size={14} />
                              <span>Securely stored (enter new key to update)</span>
                            </div>
                          )}
                          <div className="relative">
                            <input
                              type={field.type === 'password' && !showApiKey[provider.id] ? 'password' : 'text'}
                              value={String(integrations[provider.id as keyof typeof integrations][field.name as keyof typeof integrations[keyof typeof integrations]] || '')}
                              onChange={(e) => updateIntegrationField(provider.id as keyof typeof integrations, field.name, e.target.value)}
                              placeholder={integrations[provider.id as keyof typeof integrations].hasStoredKey && field.type === 'password' ? 'Enter new key to update...' : field.placeholder}
                              autoComplete="off"
                              className={`w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:${getColorClasses().border} pr-12`}
                            />
                            {field.type === 'password' && (
                              <button
                                onClick={() => toggleApiKeyVisibility(provider.id)}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                              >
                                {showApiKey[provider.id] ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  
                  <div className="flex gap-3 pt-2">
                    {integrations[provider.id as keyof typeof integrations].hasStoredKey && (
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => testIntegrationConnection(provider.id as keyof typeof integrations)}
                        className="px-4 py-2 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 flex items-center gap-2"
                      >
                        <FiRefreshCw size={16} />
                        Test Connection
                      </motion.button>
                    )}
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => saveIntegrationKey(provider.id as keyof typeof integrations)}
                      disabled={(() => {
                        const integration = integrations[provider.id as keyof typeof integrations];
                        return !('apiKey' in integration && integration.apiKey);
                      })()}
                      className="px-4 py-2 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FiSave size={16} />
                      {integrations[provider.id as keyof typeof integrations].hasStoredKey ? 'Update Key' : 'Save Key'}
                    </motion.button>
                    {integrations[provider.id as keyof typeof integrations].hasStoredKey && (
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => deleteIntegrationKey(provider.id as keyof typeof integrations)}
                        className="px-4 py-2 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 flex items-center gap-2"
                      >
                        <FiTrash2 size={16} />
                        Delete Key
                      </motion.button>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
            ))}
          </div>
        </div>

        {/* CRM Providers Section */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
            🏢 CRM Integrations
          </h3>
          <div className="p-4 bg-gradient-to-r from-orange-500/10 to-red-500/10 rounded-lg border border-orange-500/20 mb-4">
            <h4 className="text-white font-semibold mb-2 flex items-center gap-2">
              📊 Sync Leads & Contacts
            </h4>
            <p className="text-gray-300 text-sm">
              Connect your CRM to automatically sync leads, contacts, and conversation data for better customer management.
            </p>
          </div>
          <div className="space-y-4">
            {crmProviders.map((provider) => (
              <motion.div
                key={provider.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-6 bg-gray-800/50 rounded-lg border border-gray-700"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-3 rounded-lg bg-gradient-to-r ${provider.color} text-white text-xl`}>
                      {provider.icon}
                    </div>
                    <div>
                      <h4 className="text-white font-semibold text-lg">{provider.name}</h4>
                      <p className="text-gray-400 text-sm">{provider.description}</p>
                    </div>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => toggleIntegration(provider.id as keyof typeof integrations)}
                    className={`text-2xl ${integrations[provider.id as keyof typeof integrations].enabled ? getColorClasses().accent : 'text-gray-600'}`}
                  >
                    {integrations[provider.id as keyof typeof integrations].enabled ? <FiToggleRight /> : <FiToggleLeft />}
                  </motion.button>
                </div>

                <AnimatePresence>
                  {integrations[provider.id as keyof typeof integrations].enabled && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="space-y-4 pt-4 border-t border-gray-700"
                    >
                      {provider.fields.map((field) => (
                        <div key={field.name}>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            {field.label}
                          </label>
                          {field.type === 'select' ? (
                            <select
                              value={(integrations[provider.id as keyof typeof integrations] as any)[field.name] || ''}
                              onChange={(e) => updateIntegrationField(provider.id as keyof typeof integrations, field.name, e.target.value)}
                              className={`w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:${getColorClasses().border}`}
                            >
                              {field.options?.map(option => (
                                <option key={option} value={option}>{option}</option>
                              ))}
                            </select>
                          ) : (
                            <div className="space-y-2">
                              {field.type === 'password' && integrations[provider.id as keyof typeof integrations].hasStoredKey && (
                                <div className="flex items-center gap-2 text-sm text-green-400">
                                  <FiShield size={14} />
                                  <span>Securely stored (enter new key to update)</span>
                                </div>
                              )}
                              <div className="relative">
                                <input
                                  type={field.type === 'password' && !showApiKey[provider.id] ? 'password' : 'text'}
                                  value={(integrations[provider.id as keyof typeof integrations] as any)[field.name] || ''}
                                  onChange={(e) => updateIntegrationField(provider.id as keyof typeof integrations, field.name, e.target.value)}
                                  placeholder={integrations[provider.id as keyof typeof integrations].hasStoredKey && field.type === 'password' ? 'Enter new key to update...' : field.placeholder}
                                  autoComplete="off"
                                  className={`w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:${getColorClasses().border} pr-12`}
                                />
                                {field.type === 'password' && (
                                  <button
                                    onClick={() => toggleApiKeyVisibility(provider.id)}
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                                  >
                                    {showApiKey[provider.id] ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                      
                      <div className="flex gap-3 pt-2">
                        {integrations[provider.id as keyof typeof integrations].hasStoredKey && (
                          <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => testIntegrationConnection(provider.id as keyof typeof integrations)}
                            className="px-4 py-2 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 flex items-center gap-2"
                          >
                            <FiRefreshCw size={16} />
                            Test Connection
                          </motion.button>
                        )}
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => saveIntegrationKey(provider.id as keyof typeof integrations)}
                          disabled={(() => {
                            const integration = integrations[provider.id as keyof typeof integrations];
                            return !('apiKey' in integration && integration.apiKey);
                          })()}
                          className="px-4 py-2 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <FiSave size={16} />
                          {integrations[provider.id as keyof typeof integrations].hasStoredKey ? 'Update Key' : 'Save Key'}
                        </motion.button>
                        {integrations[provider.id as keyof typeof integrations].hasStoredKey && (
                          <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => deleteIntegrationKey(provider.id as keyof typeof integrations)}
                            className="px-4 py-2 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 flex items-center gap-2"
                          >
                            <FiTrash2 size={16} />
                            Delete Key
                          </motion.button>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="p-4 bg-gray-800/30 rounded-lg border border-gray-700">
          <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
            <FiSettings className={getColorClasses().accent} />
            Global AI Settings
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Default Provider</label>
              <select className={`w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:${getColorClasses().border}`}>
                <option>Auto-select best provider</option>
                <option>OpenAI</option>
                <option>Anthropic</option>
                <option>Google AI</option>
                <option>Azure OpenAI</option>
                <option>Ollama</option>
                <option>Hugging Face</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Fallback Strategy</label>
              <select className={`w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:${getColorClasses().border}`}>
                <option>Use cheapest available</option>
                <option>Use fastest available</option>
                <option>Disable fallback</option>
              </select>
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  const renderPhoneSection = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="space-y-6"
    >
      {/* Phone Numbers Section */}
      <div className="p-6 bg-gray-800/50 rounded-lg border border-gray-700">
        <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
          <FiPhone className={getColorClasses().accent} />
          Phone Numbers
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Primary Phone</label>
            <input
              type="tel"
              value={phoneSettings.primaryPhone}
              onChange={(e) => setPhoneSettings(prev => ({ ...prev, primaryPhone: e.target.value }))}
              placeholder="+1 (555) 123-4567"
              className={`w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:${getColorClasses().border}`}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Secondary Phone</label>
            <input
              type="tel"
              value={phoneSettings.secondaryPhone}
              onChange={(e) => setPhoneSettings(prev => ({ ...prev, secondaryPhone: e.target.value }))}
              placeholder="+1 (555) 987-6543"
              className={`w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:${getColorClasses().border}`}
            />
          </div>
        </div>
      </div>

      {/* Channel Settings */}
      <div className="p-6 bg-gray-800/50 rounded-lg border border-gray-700">
        <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
          <FiSettings className={getColorClasses().accent} />
          Communication Channels
        </h4>
        <div className="space-y-4">
          {[
            { key: 'smsEnabled', label: 'SMS Messages', icon: '💬', description: 'Send and receive text messages' },
            { key: 'voiceEnabled', label: 'Voice Calls', icon: '📞', description: 'Handle voice calls and voicemail' },
            { key: 'whatsappEnabled', label: 'WhatsApp', icon: '📱', description: 'Connect via WhatsApp Business API' }
          ].map((channel) => (
            <div key={channel.key} className="flex items-center justify-between p-4 bg-gray-800 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gray-700">
                  <span className="text-lg">{channel.icon}</span>
                </div>
                <div>
                  <h5 className="text-white font-medium">{channel.label}</h5>
                  <p className="text-gray-400 text-sm">{channel.description}</p>
                </div>
              </div>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setPhoneSettings(prev => ({ 
                  ...prev, 
                  [channel.key]: !prev[channel.key as keyof typeof prev] 
                }))}
                className={`text-2xl ${phoneSettings[channel.key as keyof typeof phoneSettings] ? getColorClasses().accent : 'text-gray-600'}`}
              >
                {phoneSettings[channel.key as keyof typeof phoneSettings] ? <FiToggleRight /> : <FiToggleLeft />}
              </motion.button>
            </div>
          ))}
        </div>
      </div>

      {/* Provider Configuration */}
      <div className="p-6 bg-gray-800/50 rounded-lg border border-gray-700">
        <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
          <FiGlobe className={getColorClasses().accent} />
          Service Provider
        </h4>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Provider</label>
            <select
              value={phoneSettings.provider}
              onChange={(e) => setPhoneSettings(prev => ({ ...prev, provider: e.target.value }))}
              className={`w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:${getColorClasses().border}`}
            >
              <option value="twilio">Twilio</option>
              <option value="vonage">Vonage (Nexmo)</option>
              <option value="custom">Custom Provider</option>
            </select>
          </div>

          <AnimatePresence>
            {phoneSettings.provider === 'twilio' && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="space-y-4"
              >
                <div className="p-4 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-lg border border-blue-500/20">
                  <h5 className="text-white font-semibold mb-2">Twilio Configuration</h5>
                  <p className="text-gray-300 text-sm">
                    Configure your Twilio account for SMS and voice services. Get your credentials from the Twilio Console.
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Account SID</label>
                    <input
                      type="text"
                      value={phoneSettings.providerConfig.accountSid}
                      onChange={(e) => setPhoneSettings(prev => ({ 
                        ...prev, 
                        providerConfig: { ...prev.providerConfig, accountSid: e.target.value } 
                      }))}
                      placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                      className={`w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:${getColorClasses().border}`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Auth Token</label>
                    <div className="relative">
                      <input
                        type={showApiKey.twilio ? 'text' : 'password'}
                        value={phoneSettings.providerConfig.authToken}
                        onChange={(e) => setPhoneSettings(prev => ({ 
                          ...prev, 
                          providerConfig: { ...prev.providerConfig, authToken: e.target.value } 
                        }))}
                        placeholder="Your Twilio Auth Token"
                        autoComplete="off"
                        className={`w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:${getColorClasses().border} pr-12`}
                      />
                      <button
                        onClick={() => toggleApiKeyVisibility('twilio')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                      >
                        {showApiKey.twilio ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                      </button>
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Phone Number</label>
                  <input
                    type="tel"
                    value={phoneSettings.providerConfig.phoneNumber}
                    onChange={(e) => setPhoneSettings(prev => ({ 
                      ...prev, 
                      providerConfig: { ...prev.providerConfig, phoneNumber: e.target.value } 
                    }))}
                    placeholder="+15551234567"
                    className={`w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:${getColorClasses().border}`}
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="flex gap-3 pt-4">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => toast.success('Phone configuration tested successfully!')}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 flex items-center gap-2"
            >
              <FiRefreshCw size={16} />
              Test Configuration
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => toast.success('Phone settings saved!')}
              className="px-4 py-2 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 flex items-center gap-2"
            >
              <FiSave size={16} />
              Save Settings
            </motion.button>
          </div>
        </div>
      </div>

      {/* Usage Statistics */}
      <div className="p-6 bg-gray-800/50 rounded-lg border border-gray-700">
        <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
          <FiDatabase className="text-orange-400" />
          Usage Statistics
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-gray-700 rounded-lg text-center">
            <p className="text-2xl font-bold text-blue-400">1,247</p>
            <p className="text-sm text-gray-400">SMS Sent</p>
          </div>
          <div className="p-4 bg-gray-700 rounded-lg text-center">
            <p className="text-2xl font-bold text-green-400">89</p>
            <p className="text-sm text-gray-400">Voice Calls</p>
          </div>
          <div className="p-4 bg-gray-700 rounded-lg text-center">
            <p className="text-2xl font-bold text-purple-400">$47.23</p>
            <p className="text-sm text-gray-400">Monthly Cost</p>
          </div>
        </div>
      </div>
    </motion.div>
  );

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'profile': return renderProfileSection();
      case 'security': return renderSecuritySection();
      case 'notifications': return renderNotificationsSection();
      case 'appearance': return renderAppearanceSection();
      case 'integrations': return renderIntegrationsSection();
      case 'phone': return renderPhoneSection();
      case 'advanced':
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div className="p-4 bg-gray-800 rounded-lg">
              <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                <FiDatabase className="text-blue-400" />
                Data Management
              </h4>
              <div className="space-y-3">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full p-3 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 flex items-center justify-center gap-2"
                >
                  <FiRefreshCw size={16} />
                  Export Data
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full p-3 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 flex items-center justify-center gap-2"
                >
                  <FiTrash2 size={16} />
                  Delete Account
                </motion.button>
              </div>
            </div>
          </motion.div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-6">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              rotate: [0, 360],
              opacity: [0.1, 0.3, 0.1],
              scale: [1, 1.2, 1],
            }}
            transition={{
              duration: 15 + i * 2,
              repeat: Infinity,
              delay: i * 0.5,
            }}
          >
            <FiSettings size={30 + i * 5} className={`${getColorClasses().accent}/20`} />
          </motion.div>
        ))}
      </div>

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className={`text-4xl font-bold bg-gradient-to-r ${getColorClasses().gradient} bg-clip-text text-transparent`}>
            Settings
          </h1>
          <p className="text-gray-400 mt-2">Customize your account and preferences</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-2"
          >
            {sections.map((section, index) => (
              <motion.button
                key={section.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.02, x: 5 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setActiveSection(section.id)}
                className={`w-full p-4 rounded-lg text-left transition-all ${
                  activeSection === section.id
                    ? `bg-gradient-to-r ${getColorClasses().primary} text-white`
                    : 'bg-gray-800/50 text-gray-300 hover:bg-gray-700/50'
                }`}
              >
                <div className="flex items-center gap-3 mb-1">
                  {section.icon}
                  <span className="font-medium">{section.title}</span>
                </div>
                <p className="text-xs opacity-75">{section.description}</p>
              </motion.button>
            ))}
          </motion.div>

          {/* Content */}
          <div className="lg:col-span-3">
            <motion.div
              key={activeSection}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-6 rounded-2xl"
              style={{
                background: 'rgba(30, 41, 59, 0.8)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <div className="mb-6">
                <h2 className="text-2xl font-semibold text-white mb-2">
                  {sections.find(s => s.id === activeSection)?.title}
                </h2>
                <p className="text-gray-400 text-sm">
                  {sections.find(s => s.id === activeSection)?.description}
                </p>
              </div>

              {renderActiveSection()}

              {/* Save Button */}
              {(activeSection === 'profile' || activeSection === 'security' || activeSection === 'integrations' || activeSection === 'phone') && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="flex gap-3 mt-8 pt-6 border-t border-gray-700"
                >
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => toast.success('Settings saved successfully!')}
                    className={`px-6 py-3 bg-gradient-to-r ${getColorClasses().primary} text-white rounded-lg font-medium flex items-center gap-2`}
                  >
                    <FiSave size={16} />
                    Save Changes
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="px-6 py-3 bg-gray-700 text-gray-300 rounded-lg font-medium hover:bg-gray-600"
                  >
                    Cancel
                  </motion.button>
                </motion.div>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
